import { AxiosError } from 'axios';
import classNames from 'classnames';
import { isEqual } from 'lodash';
import React, {
  useState, useEffect, useMemo, useCallback,
} from 'react';
import Skeleton from 'react-loading-skeleton';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Row, Col,
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
} from 'reactstrap';

import {
  markAsUrgentDispatch,
} from '@reducers/referral/ReferralAction';
import { handleError } from '@services/ErrorHandler';
import LocaleService from '@services/LocaleService';
import { CustomButton } from '@shared/buttons';
import Alert from '@shared/components/alert/Alert';
import CustomWysiwyg from '@shared/components/custom-wysiwyg/CustomWysiwyg';
import Checkbox from '@shared/components/input/Checkbox';
import InputField from '@shared/components/input/InputField';
import {
  cleanText, stripHtmlTags, trimWysiwygValue,
} from '@shared/helpers';
import useFormPrompt from '@shared/hooks/useFormPrompt';
import { SavedIcon, InfoFilledIcon } from '@shared/icons';

import { AllergiesInfoSkeleton, ConsiderationsInfoSkeleton, SedationsInfoSkeleton } from './skeleton-loading/PatientHistorySkeleton';
import { useGuestReferral, useReferralStepRequests } from '../guest/GuestReferralContext';

import '../SendReferralOrderPageStyle.scss';

const maxLength = 2000;

type PatientHistoryFormProps = {
  open: string;
  toggle: (e: any) => void;
  loadingDetails: boolean;
  beforeNext?: (latestReferral: any) => Promise<boolean>;
};

const specialLabels = [ 'Antibiotics', 'Pain Medicine', 'Others' ];

const PatientHistoryForm = ({
  open, toggle, loadingDetails, beforeNext,
}: PatientHistoryFormProps) => {
  const dispatch = useDispatch<any>();
  const location = useLocation();
  const navigate = useNavigate();
  const requests = useReferralStepRequests();
  const guest = useGuestReferral();
  const isUpdate = location?.pathname?.includes('view');
  const returnTo = location?.state?.returnTo;
  const i18n = LocaleService.getTranslations('referrals.sendReferral');
  const referralDetails = useSelector(({ Referral }) => Referral.referralData);
  const isUrgent = useSelector(({ Referral }) => Referral.isUrgent);
  const isCompleted = referralDetails?.status === 'completed';
  const [ allergyOptions, setAllergyOptions ] = useState<any>([]);
  const [ considerationOptions, setConsiderationOptions ] = useState<any>([]);
  const [ sedationOptions, setSedationOptions ] = useState<any>([]);

  const [ formData, setFormData ] = useState<any>({});

  const [ allergies, setAllergies ] = useState<string[]>([]);
  const [ considerations, setConsiderations ] = useState<string[]>([]);
  const [ sedations, setSedations ] = useState<string[]>([]);
  const [ errors, setErrors ] = useState<any>([]);
  const [ loading, setLoading ] = useState(false);
  const [ saved, setSaved ] = useState(false);
  const [ invalid, setInvalid ] = useState(false);
  const [ loadingOptions, setLoadingOptions ] = useState<any>({
    loadingAllergies: true,
    loadingConsiderations: true,
    loadingSedations: true,
  });
  const [ isSaving, setIsSaving ] = useState(false);

  const initialData = useMemo(() => {
    if (open !== '3') return {}; // Return empty object if accordion is closed

    return {
      referral_id: referralDetails?.id ?? '',
      patient_id: referralDetails?.patient?.id ?? '',
      medical_history: referralDetails?.patient?.medical_record?.medical_history ?? '',
      allergies: referralDetails?.patient?.medical_record?.allergies ?? [],
      special_considerations: referralDetails?.patient?.medical_record
        ?.special_considerations ?? [],
      need_sedations: referralDetails?.patient?.medical_record?.need_sedations ?? [],
      is_urgent: referralDetails?.is_urgent || false,
    };
  }, [
    open,
    referralDetails,
  ]);

  const isDirty = useCallback(() => {
    if (open !== '3') return false; // Skip the check if accordion is closed
    const newData = {
      referral_id: referralDetails?.id ?? '',
      patient_id: referralDetails?.patient?.id ?? '',
      medical_history: formData.medical_history ?? '',
      allergies: allergies ?? [],
      special_considerations: considerations ?? [],
      need_sedations: sedations ?? [],
      is_urgent: isUrgent || referralDetails?.is_urgent || false,
    };

    return !isEqual(newData, initialData);
  }, [
    initialData, formData, open,
    allergies, considerations, sedations,
    referralDetails, isUrgent,
  ]);

  const loadAllergies = async () => {
    setLoadingOptions((prevState: any) => ({
      ...prevState,
      loadingAllergies: true,
    }));
    try {
      const result = await dispatch(requests.getAllergiesRequest()).$promise;
      // const allergiesRes = result.data?.map((allergy: any) => ({
      //   label: allergy.name,
      //   value: allergy.value,
      //   key: allergy.id,
      // })) || [];
      const currentAllergies = referralDetails?.patient?.medical_record?.allergies ?? [];
      const allergiesRes = currentAllergies.reduce((acc: any, value: string) => {
        // Check if value already exists in the result data
        if (!result.data?.data?.some((allergy: any) => allergy.value === value)) {
          acc.push({
            label: value,
            value,
            // Temporary key for deleted items
            key: `temp-${value}`,
          });
        }
        return acc;
      }, result.data?.data?.map((allergy: any) => ({
        label: allergy.name,
        value: allergy.value,
        key: allergy.id,
      })) || []);
      setAllergyOptions(allergiesRes);
    } catch (err) {
      handleError(err);
    } finally {
      setLoadingOptions((prevState: any) => ({
        ...prevState,
        loadingAllergies: false,
      }));
    }
  };

  const loadConsiderations = async () => {
    setLoadingOptions((prevState: any) => ({
      ...prevState,
      loadingConsiderations: true,
    }));
    try {
      const result = await dispatch(requests.getSpecialConsiderationsRequest()).$promise;
      // const considerationsRes = result.data?.map((con: any) => ({
      //   label: con.name,
      //   value: con.value,
      //   key: con.id,
      // })) || [];
      const currConsiderations = referralDetails?.patient?.medical_record?.special_considerations
        ?? [];
      const considerationsRes = currConsiderations.reduce((acc: any, value: string) => {
        // Check if value already exists in the result data
        if (!result.data?.data?.some((con: any) => con.value === value)) {
          acc.push({
            label: value,
            value,
            // Temporary key for deleted items
            key: `temp-${value}`,
          });
        }
        return acc;
      }, result.data?.data?.map((con: any) => ({
        label: con.name,
        value: con.value,
        key: con.id,
      })) || []);
      setConsiderationOptions(considerationsRes);
    } catch (err) {
      handleError(err);
    } finally {
      setLoadingOptions((prevState: any) => ({
        ...prevState,
        loadingConsiderations: false,
      }));
    }
  };

  const loadSedations = async () => {
    setLoadingOptions((prevState: any) => ({
      ...prevState,
      loadingSedations: true,
    }));
    try {
      const result = await dispatch(requests.getSedationsRequest()).$promise;
      // const sedationsRes = result.data?.map((sed: any) => ({
      //   label: sed.name,
      //   value: sed.value,
      //   key: sed.id,
      // })) || [];
      const currSedations = referralDetails?.patient?.medical_record?.need_sedations ?? [];
      const sedationsRes = currSedations.reduce((acc: any, value: string) => {
        // Check if value already exists in the result data
        if (!result.data?.data?.some((sed: any) => sed.value === value)) {
          acc.push({
            label: value,
            value,
            // Temporary key for deleted items
            key: `temp-${value}`,
          });
        }
        return acc;
      }, result.data?.data?.map((sed: any) => ({
        label: sed.name,
        value: sed.value,
        key: sed.id,
      })) || []);
      setSedationOptions(sedationsRes);
    } catch (err) {
      handleError(err);
    } finally {
      setLoadingOptions((prevState: any) => ({
        ...prevState,
        loadingSedations: false,
      }));
    }
  };

  const handleAllergiesChange = (label: string) => {
    let newAllergies = [ ...allergies ];
    // check if a certain allergy is existing in the array
    const isExists = newAllergies.filter(allergy => allergy.startsWith(label));

    // if existing, remove it if unchecked
    if (isExists.length > 0) {
      newAllergies = newAllergies.filter(allergy => !allergy.startsWith(label));
      setAllergies(newAllergies);
      return;
    }

    // else push the allergy value in the array
    let allergyDetail = label;
    // if with input details, concat input value
    if (specialLabels.includes(label)) {
      const detail = formData[label];
      if (detail) {
        allergyDetail = `${label}: ${detail}`;
      }
    }
    newAllergies.push(allergyDetail);

    setAllergies(newAllergies);
  };

  const handleAllergyDetailChange = (label: string, value: string) => {
    setFormData((prevState: any) => ({
      ...prevState,
      [label]: value,
    }));

    // Update selected special allergy; dedupe against existing non-special selections
    const raw = typeof value === 'string' ? value : '';
    const trimmed = raw.trim();
    if (allergies.some(a => a.startsWith(label))) {
      setAllergies(prev => {
        const hasExactDuplicate = prev.some(a => !specialLabels
          .some(sl => a.startsWith(sl)) && a.toLowerCase() === trimmed.toLowerCase(),
        );
        const nextDetail = hasExactDuplicate ? '' : trimmed;
        return prev.map(a => {
          if (a.startsWith(label)) {
            return nextDetail ? `${label}: ${nextDetail}` : label;
          }
          return a;
        });
      });
    }
  };

  const handleConsiderationsChange = (value: string) => {
    setConsiderations(prevState => (prevState.includes(value)
      ? prevState.filter(cons => cons !== value)
      : [ ...prevState, value ]),
    );
  };

  const handleSedationsChange = (value: string) => {
    setSedations(prevState => (prevState.includes(value)
      ? prevState.filter(sed => sed !== value)
      : [ ...prevState, value ]),
    );
  };

  const handleInputChange = (value: any) => {
    let newValue = value;
    // Trim leading and trailing spaces/tags from the value
    newValue = newValue.trim();
    newValue = trimWysiwygValue(newValue);

    setFormData((prevState: any) => ({
      ...prevState,
      medical_history: cleanText(newValue), // Remove invisible zero-width characters
    }));
  };

  const savePatientHistory = async (toggleValue: string) => {
    setLoading(true);
    setInvalid(false);
    setErrors([]);
    // message will prompt that the user should
    // fill out Patient Information (step 2) first
    if (!referralDetails?.patient?.id) {
      setInvalid(true);
      setLoading(false);
      return;
    }
    const countMedicalHistoryString = stripHtmlTags(formData.medical_history)?.length;

    const data = {
      referral_id: referralDetails?.id,
      patient_id: referralDetails?.patient?.id,
      medical_history: countMedicalHistoryString > 0 ? formData.medical_history : '',
      allergies,
      special_considerations: considerations,
      need_sedations: sedations,
      is_urgent: isUrgent || referralDetails?.is_urgent || false,
    };
    try {
      const request = referralDetails?.patient?.medical_record?.id
        ? requests.updatePatientHistoryRequest(data, referralDetails.patient.medical_record.id)
        : requests.createPatientHistoryRequest(data);

      const result = await dispatch(request).$promise;
      setSaved(true);
      if (toggleValue === '4' && beforeNext) {
        // Phase 3 (backend): now answers {data: <ReferralResource>} instead of it directly.
        const canContinue = await beforeNext(result.data?.data);
        if (!canContinue) return;
      }
      if (toggleValue !== '0') toggle(toggleValue);
    } catch (error) {
      const { response: errorResponse } = error as AxiosError;

      if (errorResponse) {
        if (errorResponse.status === 422) {
          const { errors: errorData } = errorResponse.data;
          setErrors(errorData);
          return;
        }
        handleError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const onHandleNext = async () => {
    await savePatientHistory('4');
  };

  const onSaveExit = async (e?: any) => {
    e?.stopPropagation();
    if (!isDirty()) {
      navigate(returnTo);
      return;
    }
    setIsSaving(true);
    setInvalid(false);
    setErrors([]);
    // message will prompt that the user should
    // fill out Patient Information (step 2) first
    if (!referralDetails?.patient?.id) {
      setInvalid(true);
      setLoading(false);
      return;
    }
    const countMedicalHistoryString = stripHtmlTags(formData.medical_history)?.length;
    const data = {
      referral_id: referralDetails?.id,
      patient_id: referralDetails?.patient?.id,
      medical_history: countMedicalHistoryString > 0 ? formData.medical_history : '',
      allergies,
      special_considerations: considerations,
      need_sedations: sedations,
      is_urgent: isUrgent || referralDetails?.is_urgent || false,
    };
    try {
      const request = referralDetails?.patient?.medical_record?.id
        ? requests.updatePatientHistoryRequest(data, referralDetails.patient.medical_record.id)
        : requests.createPatientHistoryRequest(data);

      await dispatch(request).$promise;
      setSaved(true);
      navigate(returnTo);
    } catch (error) {
      const { response: errorResponse } = error as AxiosError;

      if (errorResponse) {
        if (errorResponse.status === 422) {
          const { errors: errorData } = errorResponse.data;
          setErrors(errorData);
          return;
        }
        handleError(error);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const onDiscardChanges = () => {
    dispatch(markAsUrgentDispatch(referralDetails?.is_urgent || false));

    setFormData((prevState: any) => ({
      ...prevState,
      medical_history: referralDetails?.patient?.medical_record?.medical_history || '',
      is_urgent: referralDetails?.is_urgent || false,
    }));

    setConsiderations(referralDetails?.patient?.medical_record?.special_considerations || []);
    setSedations(referralDetails?.patient?.medical_record?.need_sedations || []);

    const allergiesArr = referralDetails?.patient?.medical_record?.allergies;
    setAllergies(allergiesArr || []);

    if (!allergiesArr || allergiesArr.length === 0) return;

    const newFormData: { [key: string]: string } = {};

    allergiesArr.forEach((d: any) => {
      if (d.includes(':')) {
        const splitText = d.split(':');
        if (splitText.length === 0) return;
        const formLabel = splitText[0];
        const formValue = splitText[1];
        newFormData[formLabel] = formValue;
      }
    });

    setFormData((prevState: any) => ({
      ...prevState,
      ...newFormData,
    }));
  };

  const { confirm } = useFormPrompt({
    onSave: async () => {
      // Save form logic
      await savePatientHistory('3');
    },
    onDiscard: () => {
      // Discard changes logic
      onDiscardChanges();
    },
    // isFormDirty: isDirty,
    isFormDirty: isDirty(),
  });

  const toggleAccordion = async () => {
    // Call confirm prompt before closing accordion
    // if there are any changes
    if (open === '3' && isDirty()) {
      const result = await confirm();
      if (!result) return;
    }
    toggle('3');
  };

  const onHandleBack = async () => {
    if (isDirty()) {
      const result = await confirm();
      if (!result) return;
    }
    // A guest (ENH-052) has no page to return to: Back is the previous step.
    if (guest) {
      toggle('2');
      return;
    }
    navigate(returnTo);
  };

  useEffect(() => {
    if (allergyOptions.length > 0 || open !== '3') return;
    loadAllergies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ allergyOptions, open ]);

  useEffect(() => {
    if (considerationOptions.length > 0 || open !== '3') return;
    loadConsiderations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ considerationOptions, open ]);

  useEffect(() => {
    if (sedationOptions.length > 0 || open !== '3') return;
    loadSedations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ sedationOptions, open ]);

  // set default data for checkboxes
  useEffect(() => {
    const initiate = () => {
      if (open !== '3') return;

      setFormData((prevState: any) => ({
        ...prevState,
        is_urgent: isUrgent || referralDetails?.is_urgent || false,
        medical_history: formData?.medical_history
          || referralDetails?.patient?.medical_record?.medical_history,
      }));

      const newAllergies = referralDetails?.patient?.medical_record?.allergies
        || allergies;

      setAllergies(newAllergies ?? []);

      const newConsiderations = referralDetails?.patient?.medical_record?.special_considerations
        || considerations;

      setConsiderations(newConsiderations ?? []);

      const newSedations = referralDetails?.patient?.medical_record?.need_sedations
        || sedations;

      setSedations(newSedations ?? []);
    };
    initiate();

    // clear data when component unmounts
    return () => {
      // setFormData({});
      // setAllergies([]);
      // setConsiderations([]);
      // setSedations([]);
      setInvalid(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ referralDetails?.patient?.medical_record, open ]);

  // set default data for checkboxes input fields
  useEffect(() => {
    const initiate = () => {
      if (open !== '3') return;

      const allergiesArr = referralDetails?.patient?.medical_record?.allergies
        || allergies;

      if (!allergiesArr || allergiesArr.length === 0) return;

      const newFormData: { [key: string]: string } = {};

      allergiesArr.forEach((d: any) => {
        if (d.includes(':')) {
          const splitText = d.split(':');
          if (splitText.length === 0) return;
          const formLabel = splitText[0];
          const formValue = splitText[1];
          newFormData[formLabel] = formValue;
        }
      });

      setFormData((prevState: any) => ({
        ...prevState,
        ...newFormData,
      }));
    };

    initiate();

    // clear data when component unmounts
    return () => {
      // setFormData({});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ referralDetails?.patient?.medical_record, open ]);

  return (
    <div>
      <Accordion
        open={open}
        toggle={toggleAccordion}
        className={classNames('mb-3', {
          'sr-accordion-collapsed': open === '3',
        })}
      >
        <AccordionItem>
          <AccordionHeader className="sr-accordion-header" targetId="3">
            <Row className="align-items-center">
              <Col xs={3} md={1} style={{ width: '50px' }}>
                {
                  (saved || referralDetails?.patient?.medical_record?.id) && open !== '3' ? (<SavedIcon />) : (
                    <span
                      className={classNames('sr-accordion-number', {
                        'sr-accordion-number-collapsed': open === '3',
                      })}
                    >
                      3
                    </span>
                  )
                }
              </Col>
              <Col xs={9} md={7} lg={8} xl={9}>
                <span className="sr-accordion-title">
                  {i18n.forms.patientHistory.title}
                </span>
              </Col>
              {!isCompleted && isUpdate && open === '3' && (
                <Col xs={12} md={4} lg={3} xl={2} className="sr-save-exit-container">
                  <CustomButton
                    id="btn-save-exit"
                    className="btn-lg sr-save-exit-btn"
                    color="primary"
                    onClick={(e: any) => onSaveExit(e)}
                    disabled={loading || loadingDetails || isSaving}
                    loading={isSaving}
                  >
                    {i18n.button.saveExit}
                  </CustomButton>
                </Col>
              )}
            </Row>
          </AccordionHeader>
          <AccordionBody accordionId="3">
            {invalid && (
              <Alert color="error">{LocaleService.parseTranslation(i18n.invalid, {
                ':number': 2,
              })}
              </Alert>
            )}
            <Row>
              <Col xs={1} style={{ width: '50px' }} />
              <Col xs={12} lg={11}>
                {/* PATIENT MEDICAL HISTORY */}
                <div className="mb-4">
                  <Row className="mb-3">
                    <Col>
                      <span className="sr-form-title">{i18n.forms.patientHistory.medicalInfo.title}</span>
                    </Col>
                  </Row>
                  {loadingDetails && (
                    <Row className="mb-3">
                      <Col>
                        <Skeleton
                          borderRadius={10}
                          baseColor="var(--skeleton-base)"
                          highlightColor="var(--skeleton-highlight)"
                          height={80}
                        />
                      </Col>
                    </Row>
                  )}
                  {!loadingDetails && (
                    <Row className="">
                      <Col>
                        <CustomWysiwyg
                          content={formData?.medical_history}
                          inputChange={handleInputChange}
                          errors={errors?.medical_history}
                          placeholder={
                            i18n.forms.patientHistory.medicalInfo.history.placeholder
                          }
                          maxLength={maxLength}
                          readOnly={isCompleted}
                        />
                      </Col>
                    </Row>
                  )}
                </div>
                {/* PATIENT ALLERGIES */}
                <div className="mb-4">
                  <Row className="mb-3">
                    <Col>
                      <span className="sr-form-title">{i18n.forms.patientHistory.allergyInfo.title}</span>
                      <span className="sr-form-title-placeholder">{i18n.forms.patientHistory.allergyInfo.placeholder}</span>
                    </Col>
                  </Row>
                  {(loadingDetails || loadingOptions.loadingAllergies) && (
                    <AllergiesInfoSkeleton />
                  )}
                  {!loadingDetails && !loadingOptions.loadingAllergies && (
                    <Row className="mb-2">
                      <Col lg={6}>
                        {allergyOptions && allergyOptions.length > 0
                          && allergyOptions.slice(0, Math.ceil(allergyOptions.length / 2))
                            .map((d: any) => (
                              <Row key={d.id} className="mb-3">
                                <Col xs={12}>
                                  {specialLabels.includes(d.label) ? (
                                    <Row>
                                      <Col xs={12} md={4} className="allergies-with-input-container">
                                        <Checkbox
                                          id={d.label}
                                          name={d.label}
                                          type="checkbox"
                                          formGrpClassName="p-history-check-group"
                                          checked={allergies && allergies.length > 0 && allergies
                                            .some(allergy => allergy.startsWith(d.label))}
                                          label={d.label}
                                          onInputChangeHandler={() => handleAllergiesChange(
                                            d.label,
                                          )}
                                          value={d.label}
                                          isDisabled={isCompleted}
                                        />
                                      </Col>
                                      {allergies && allergies.length > 0 && allergies
                                        .some(allergy => allergy.startsWith(d.label)) && (
                                        <Col xs={12} md={8} className="allergies-input-container">
                                          <InputField
                                            id={`send-ref-allergy-input-${d.label}`}
                                            name={d.label}
                                            customClassName="allergies-input"
                                            formGrpClassName="ph-form-grp"
                                            placeholder={d.label === 'Others' ? i18n.forms.patientHistory.allergyInfo.others.placeholder
                                              : LocaleService.parseTranslation(
                                                i18n.forms.patientHistory.allergyInfo
                                                  .input.placeholder,
                                                { ':type': d.label.toLowerCase() })}
                                            type="text"
                                            defaultValue={formData[d.label] ?? ''}
                                            value={formData[d.label] ?? ''}
                                            onInputChangeHandler={(
                                              value,
                                            ) => handleAllergyDetailChange(
                                              d.label,
                                              value,
                                            )}
                                            float={false}
                                            isReadOnly={isCompleted}
                                          />
                                        </Col>
                                      )}
                                    </Row>
                                  ) : (
                                    <Checkbox
                                      id={d.label}
                                      name={d.label}
                                      type="checkbox"
                                      formGrpClassName="p-history-check-group"
                                      checked={allergies && allergies.length > 0
                                        && allergies.includes(d.label)}
                                      label={d.label}
                                      onInputChangeHandler={() => handleAllergiesChange(
                                        d.label,
                                      )}
                                      value={d.label}
                                      isDisabled={isCompleted}
                                    />
                                  )}
                                </Col>
                              </Row>
                            ))}
                      </Col>
                      <Col lg={6}>
                        {allergyOptions && allergyOptions.length > 0
                          && allergyOptions.slice(Math.ceil(allergyOptions.length / 2))
                            .map((d: any) => (
                              <Row key={d.id} className="mb-3">
                                <Col xs={12}>
                                  {specialLabels.includes(d.label) ? (
                                    <Row>
                                      <Col xs={12} md={4} className="allergies-with-input-container">
                                        <Checkbox
                                          id={d.label}
                                          name={d.label}
                                          type="checkbox"
                                          formGrpClassName="p-history-check-group"
                                          checked={allergies && allergies.length > 0 && allergies
                                            .some(allergy => allergy.startsWith(d.label))}
                                          label={d.label}
                                          onInputChangeHandler={() => handleAllergiesChange(
                                            d.label,
                                          )}
                                          value={d.label}
                                          isDisabled={isCompleted}
                                        />
                                      </Col>
                                      {allergies && allergies.length > 0 && allergies
                                        .some(allergy => allergy.startsWith(d.label)) && (
                                        <Col xs={12} md={8} className="allergies-input-container">
                                          <InputField
                                            id={`send-ref-allergy-input-${d.label}`}
                                            name={d.label}
                                            customClassName="allergies-input"
                                            formGrpClassName="ph-form-grp"
                                            placeholder={d.label === 'Others' ? i18n.forms.patientHistory.allergyInfo.others.placeholder
                                              : LocaleService.parseTranslation(
                                                i18n.forms.patientHistory.allergyInfo
                                                  .input.placeholder,
                                                { ':type': d.label.toLowerCase() })}
                                            type="text"
                                            defaultValue={formData[d.label] ?? ''}
                                            value={formData[d.label] ?? ''}
                                            onInputChangeHandler={(
                                              value,
                                            ) => handleAllergyDetailChange(
                                              d.label,
                                              value,
                                            )}
                                            float={false}
                                            isReadOnly={isCompleted}
                                          />
                                        </Col>
                                      )}
                                    </Row>
                                  ) : (
                                    <Checkbox
                                      id={d.label}
                                      name={d.label}
                                      type="checkbox"
                                      formGrpClassName="p-history-check-group"
                                      checked={allergies && allergies.length > 0
                                        && allergies.includes(d.label)}
                                      label={d.label}
                                      onInputChangeHandler={() => handleAllergiesChange(
                                        d.label,
                                      )}
                                      value={d.label}
                                      isDisabled={isCompleted}
                                    />
                                  )}
                                </Col>
                              </Row>
                            ))}
                      </Col>
                    </Row>
                  )}
                </div>
                {/* PATIENT SPECIAL CONSIDERATIONS */}
                <div className="mb-4">
                  <Row className="mb-3">
                    <Col>
                      <span className="sr-form-title">{i18n.forms.patientHistory.considerationInfo.title}</span>
                      <span className="sr-form-title-placeholder">{i18n.forms.patientHistory.considerationInfo.placeholder}</span>
                    </Col>
                  </Row>
                  {(loadingDetails || loadingOptions.loadingConsiderations) && (
                    <ConsiderationsInfoSkeleton />
                  )}
                  {!loadingDetails && !loadingOptions.loadingConsiderations && (
                    <Row className="mb-2">
                      <Col lg={6}>
                        {considerationOptions && considerationOptions.length > 0
                          && considerationOptions
                            .slice(0, Math.ceil(considerationOptions.length / 2))
                            .map((d: any) => (
                              <Row key={d.id} className="mb-3">
                                <Col xs={12}>
                                  <Checkbox
                                    id={d.label}
                                    name={d.label}
                                    type="checkbox"
                                    formGrpClassName="p-history-check-group"
                                    checked={considerations && considerations.length > 0
                                      && considerations.includes(d.label)}
                                    label={d.label}
                                    onInputChangeHandler={() => handleConsiderationsChange(
                                      d.label,
                                    )}
                                    value={d.label}
                                    isDisabled={isCompleted}
                                  />
                                </Col>
                              </Row>
                            ))}
                      </Col>
                      <Col lg={6}>
                        {considerationOptions && considerationOptions.length > 0
                          && considerationOptions.slice(Math.ceil(considerationOptions.length / 2))
                            .map((d: any) => (
                              <Row key={d.id} className="mb-3">
                                <Col xs={12}>
                                  <Checkbox
                                    id={d.label}
                                    name={d.label}
                                    type="checkbox"
                                    formGrpClassName="p-history-check-group"
                                    checked={considerations && considerations.length > 0
                                      && considerations.includes(d.label)}
                                    label={d.label}
                                    onInputChangeHandler={() => handleConsiderationsChange(
                                      d.label,
                                    )}
                                    value={d.label}
                                    isDisabled={isCompleted}
                                  />
                                </Col>
                              </Row>
                            ))}
                      </Col>
                    </Row>
                  )}
                </div>
                {/* PATIENT NEED SEDATIONS */}
                <div className="mb-4">
                  <Row className="mb-3">
                    <Col>
                      <span className="sr-form-title">{i18n.forms.patientHistory.sedationInfo.title}</span>
                      <span className="sr-form-title-placeholder">{i18n.forms.patientHistory.sedationInfo.placeholder}</span>
                    </Col>
                  </Row>
                  {(loadingDetails || loadingOptions.loadingSedations) && (
                    <SedationsInfoSkeleton />
                  )}
                  {!loadingDetails && !loadingOptions.loadingSedations && (
                    <Row className="mb-2">
                      <Col lg={6}>
                        {sedationOptions && sedationOptions.length > 0
                          && sedationOptions.slice(0, Math.ceil(sedationOptions.length / 2))
                            .map((d: any) => (
                              <Row key={d.id} className="mb-3">
                                <Col xs={12}>
                                  <Checkbox
                                    id={d.label}
                                    name={d.label}
                                    type="checkbox"
                                    formGrpClassName="p-history-check-group"
                                    checked={sedations && sedations.length > 0
                                      && sedations.includes(d.label)}
                                    label={d.label}
                                    onInputChangeHandler={() => handleSedationsChange(
                                      d.label,
                                    )}
                                    value={d.label}
                                    isDisabled={isCompleted}
                                  />
                                </Col>
                              </Row>
                            ))}
                      </Col>
                      <Col lg={6}>
                        {sedationOptions && sedationOptions.length > 0
                          && sedationOptions.slice(Math.ceil(sedationOptions.length / 2))
                            .map((d: any) => (
                              <Row key={d.id} className="mb-3">
                                <Col xs={12}>
                                  <Checkbox
                                    id={d.label}
                                    name={d.label}
                                    type="checkbox"
                                    formGrpClassName="p-history-check-group"
                                    checked={sedations && sedations.length > 0
                                      && sedations.includes(d.label)}
                                    label={d.label}
                                    onInputChangeHandler={() => handleSedationsChange(
                                      d.label,
                                    )}
                                    value={d.label}
                                    isDisabled={isCompleted}
                                  />
                                </Col>
                              </Row>
                            ))}
                      </Col>
                    </Row>
                  )}
                </div>
                {!isCompleted && (
                  <div className="mb-4">
                    <div className="mb-3 sr-info-style">
                      <InfoFilledIcon />
                      <span className="sr-info-style-text">{i18n.info}</span>
                    </div>
                    <div className="sr-footer-btn-container">
                      <CustomButton
                        id="btn-back"
                        className="btn-lg sr-back-btn"
                        color="secondary"
                        onClick={onHandleBack}
                        disabled={loading || loadingDetails}
                      >
                        {i18n.button.back}
                      </CustomButton>
                      <CustomButton
                        id="btn-next"
                        className="btn-lg sr-next-btn"
                        color="primary"
                        onClick={onHandleNext}
                        disabled={loading || loadingDetails
                          || stripHtmlTags(formData?.medical_history).length > maxLength}
                        loading={loading}
                      >
                        {i18n.button.next}
                      </CustomButton>
                    </div>
                  </div>
                )}
              </Col>
            </Row>
          </AccordionBody>
        </AccordionItem>
      </Accordion>
    </div>
  );
};

export default PatientHistoryForm;
